import React, { useEffect, useState } from 'react';
import {
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
} from '@mui/material';
import { useParams } from 'react-router-dom';

import { useGetRelatedCompanies } from '../api/getRelatedCompanies';

import { RelatedCompaniesListItem } from './RelatedCompaniesListItems';
import { Pagination } from 'components/Pagination';
import { usePageSize } from 'hook/usePageSize';
import { Search } from 'components/Search';
import { useDebouncedCallback } from 'use-debounce';
import { NotFound } from 'components/NotFound/NotFound';
import { useAuth } from 'lib/auth';
import { Loader } from 'components/Loader';
import { PremiumBanner } from 'features/subscriptions/components/PremiumBanner';

export const CompaniesTab = ({ id, currentTab, companyType, companiesCount }) => {
  const companiesTabName = companyType === "buyer" ? "suppliers" : "customers";
  const { account, user } = useAuth();
  // const { id } = useParams();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize();
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(null);

  const labelName = companyType === "buyer" ? "Suppliers" : "Customers";

  const {
    data: { companies, total },
    refetch: refetchCompanies,
    isFetching,
  } = useGetRelatedCompanies(id, {
    companyType,
    search,
    includeInactive,
    page: page - 1,
    limit: pageSize,
    queryOptions: {
      onSettled: () => {
        clearTimeout(timer);
        setTimeout(() => setLoading(false), 1000);
      },
      enabled: currentTab === companiesTabName && (account.super_admin || (user.Company.is_premium && user.Company.subscription_level !== 'free'))
    },
  });

  const handlePageChange = (_, page) => setPage(page);
  const handleIncludeInactiveChange = () => setIncludeInactive(!includeInactive);
  const debouncedSearchChange = useDebouncedCallback((search) => setSearch(search), 300);
  const handleSearchChange = ({ target: { value: search } }) => {
    debouncedSearchChange(search);
  };

  useTabReset();
  useRefetchCompanies();

  useEffect(() => {
    if (isFetching) {
      const timeoutId = setTimeout(() => setLoading(true), 500);
      setTimer(timeoutId);
    } else {
      clearTimeout(timer);
    }

    return () => clearTimeout(timer);
  }, [isFetching]);

  let message = `There are ${companiesCount} more Customers available for Premium members`;
  if (companiesCount === 1) {
    message = `There is ${companiesCount} more Customer available for Premium members`
  }

  if (currentTab !== companiesTabName) return null;

  return (
    <>
      <Stack gap={2} height={1}>
        {!account.super_admin && (
          <>
            {(!user.Company.is_premium || user.Company.subscription_level === 'free') && (
              <PremiumBanner sx={{mt:-2, mb:0, ml: -4, width: { xs: 768, lg: 900, xl: 1200 }}} message={`${message}. Click here for more information`} handleSuccess={refetchCompanies} currentLevel={user.Company.subscription_level} />
            )}
          </>
        )}
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} width={1}>
          <Search handleSearchChange={handleSearchChange} sx={{minWidth:200, width: 270}} />
          {account.super_admin && (
            <>
          <FormGroup sx={{ flex: '1 1 300px' }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={includeInactive}
                  onChange={handleIncludeInactiveChange}
                />
              }
              label="Include inactive"
            />
          </FormGroup>
          </>
          )}
        </Stack>
        <Divider />

        {isFetching && !loading ? 
        (
          <></>
        ) :
        (!isFetching || (isFetching && loading)) ?
        (
          <>
          {loading && (
            <Loader />
          )}

          {!loading && (
            <>
            <Stack gap={1} flex={1}>
              {(!companies?.length || companies?.length === 0) && (
                <NotFound
                    title={`No related ${labelName} found`}
                    caption={
                    search
                        ? 'Please try another search term'
                        : ''
                    }
                    wrapperSx={{ mt: 19 }}
                />
              )}

              {companies.map(( company) => (
                <RelatedCompaniesListItem
                  key={company.company_id}
                  {...company}
                />
              ))}
            </Stack>

            <Stack direction="row" justifyContent="center">
              <Pagination
                page={page}
                handlePageChange={handlePageChange}
                itemCount={total}
                itemsPerPage={pageSize}
                onItemsPerPageChange={setPageSize}
              />
            </Stack>
            </>
          )}
        </>
        ) :
        (
          <></>
        )}
      </Stack>
    </>
  );

   function useRefetchCompanies() {
    useEffect(() => {
      if (currentTab === companiesTabName && (account.super_admin || (user.Company.is_premium && user.Company.subscription_level !== 'free'))) {
        refetchCompanies();
      }
    }, [page, pageSize, includeInactive, search]);
  }

  function useTabReset() {
    useEffect(() => {
      if (currentTab !== companiesTabName) {
        setPage(1);
        setSearch('');
        setIncludeInactive(false);
      }
    }, [currentTab]);
  }
};
